const VOICE_FILES = {
  holdOn: '/sounds/whot/voice/hold-on.mp3', continue: '/sounds/whot/voice/continue.mp3', pickTwo: '/sounds/whot/voice/pick-two.mp3',
  generalMarket: '/sounds/whot/voice/general-market.mp3', whot: '/sounds/whot/voice/whot.mp3', whotTriangle: '/sounds/whot/voice/whot-triangle.mp3',
  whotCircle: '/sounds/whot/voice/whot-circle.mp3', whotSquare: '/sounds/whot/voice/whot-square.mp3', whotStar: '/sounds/whot/voice/whot-star.mp3',
  whotCross: '/sounds/whot/voice/whot-cross.mp3', missTurn: '/sounds/whot/voice/miss-turn.mp3', yourTurn: '/sounds/whot/voice/your-turn.mp3',
  youWin: '/sounds/whot/voice/you-win.mp3', gameOver: '/sounds/whot/voice/game-over.mp3',
};

const TEXT = {
  holdOn: 'Hold 1', pickTwo: 'Pick 2', suspension: 'Suspension!', lastCard: 'Last Card!', generalMarket: 'General Market',
  whotTriangle: 'I need Triangle', whotCircle: 'I need Circle', whotSquare: 'I need Square', whotStar: 'I need Star',
  whotCross: 'I need Cross', yourTurn: 'Your turn', youWin: 'You win', gameOver: 'Game over', gameStarted: 'Game starts',
};

const DEBUG = typeof window !== 'undefined' && Boolean(window.__9JAWIN_WHOT_VOICE_DEBUG__);
const log = (...args) => { if (DEBUG) console.debug('[WhotVoice]', ...args); };
const TERMINAL_STATES = new Set(['finished', 'gameOver', 'game_over', 'completed', 'ended']);
const isTerminalState = (state) => TERMINAL_STATES.has(String(state?.state || state?.status || state?.phase || '').toLowerCase()) || Boolean(state?.gameOver || state?.isFinished || state?.ended);

const SPECIAL_KEYS = Object.freeze({ HOLD_ON: 'holdOn', PICK_TWO: 'pickTwo', GENERAL_MARKET: 'generalMarket' });
const SPECIAL_TEXT = Object.freeze({ HOLD_ON: 'Hold On!', PICK_TWO: 'Pick Two!', GENERAL_MARKET: 'General Market!' });
const SHAPE_KEYS = Object.freeze({ triangle: 'whotTriangle', circle: 'whotCircle', square: 'whotSquare', star: 'whotStar', cross: 'whotCross' });

class WhotVoiceAnnouncer {
  constructor() {
    this.enabled = true; this.volume = 0.85; this.unlocked = false;
    this.queue = []; this.playing = false; this.playingId = null; this.playingItem = null; this.playingAudio = null; this.playingUtterance = null;
    this.audio = new Map(); this.lastError = null; this.sequence = 0;
    this.interruptGeneration = 0; this.latestGameSeq = 0; this.latestGameId = null;
    this.latestGameStatus = 'unknown'; this.terminal = false; this.terminalGameId = null; this.terminalGeneration = 0;
    this.previousTurnId = null; this.previousMissedTurns = new Map(); this.previousEliminated = new Map(); this.previousCardCounts = new Map();
    this.previousActionEventId = null; this.processedEventIds = new Set(); this.latestAcceptedEventSeq = -1; this.gameStarted = false;
    this.prepareAll();
  }

  applySettings({ enabled, volume } = {}) {
    if (typeof enabled === 'boolean') this.enabled = enabled;
    if (Number.isFinite(Number(volume))) this.volume = Math.max(0, Math.min(1, Number(volume)));
    this.audio.forEach((a) => { try { a.volume = this.volume; } catch (_) {} });
    if (this.enabled) this.drain();
  }

  setEnabled(v) { this.enabled = !!v; if (!this.enabled) this.stop(); else this.drain(); }
  setVolume(v) { this.volume = Math.max(0, Math.min(1, Number(v) || 0)); this.audio.forEach((a) => { try { a.volume = this.volume; } catch (_) {} }); }

  prepare(key) {
    const src = VOICE_FILES[key]; if (!src || this.audio.has(key)) return this.audio.get(key) || null;
    try { const audio = new Audio(); audio.preload = 'auto'; audio.src = src; audio.volume = this.volume; audio.setAttribute('aria-hidden', 'true'); audio.addEventListener('error', () => { this.lastError = `Audio load failed: ${src}`; }); this.audio.set(key, audio); audio.load(); return audio; }
    catch (err) { this.lastError = `Audio init failed: ${err?.name || 'unknown'}`; return null; }
  }
  prepareAll() { Object.keys(VOICE_FILES).forEach(k => this.prepare(k)); }
  unlock() { if (this.unlocked) return; this.unlocked = true; this.prepareAll(); try { window.speechSynthesis?.resume?.(); } catch (_) {} this.drain(); }

  stop() {
    this.queue = []; this.playing = false; this.playingId = null; this.playingItem = null; this.playingAudio = null; this.playingUtterance = null; this.interruptGeneration++;
    this.audio.forEach(a => { try { a.pause(); a.currentTime = 0; a.onended = null; a.onerror = null; } catch (_) {} });
    try { window.speechSynthesis?.cancel?.(); } catch (_) {}
  }

  observeGameState(state, viewerId = null) {
    const gameId = state?.matchId ?? state?.playId ?? this.latestGameId;
    const normalizedGameId = gameId == null ? null : String(gameId);
    if (normalizedGameId && this.latestGameId && normalizedGameId !== this.latestGameId) this.resetForGame(normalizedGameId);
    if (normalizedGameId) this.latestGameId = normalizedGameId;

    const seq = Number(state?.actionSequence);
    if (Number.isFinite(seq)) this.latestGameSeq = Math.max(this.latestGameSeq, seq);
    this.latestGameStatus = String(state?.state || state?.status || state?.phase || 'unknown').toLowerCase();

    if (isTerminalState(state)) {
      this.setTerminal(normalizedGameId, { gameSeq: Number.isFinite(seq) ? seq : null, source: 'gameState' });
      if (state?.lastActionEvent?.eventId && this.processedEventIds.has(String(state.lastActionEvent.eventId))) return true;
      const terminalMeta = { gameId: normalizedGameId, gameSeq: Number.isFinite(seq) ? seq : 0, terminal: true };
      const terminalEvent = state?.lastActionEvent;
      if (terminalEvent?.type === 'TURN_TIMEOUT' && terminalEvent?.actorId != null) {
        const timeoutId = String(terminalEvent.actorId);
        const timeoutPlayer = Array.isArray(state?.players) ? state.players.find(p => String(p.userId) === timeoutId) : null;
        const viewer = viewerId == null ? null : String(viewerId);
        const timeoutText = timeoutId === viewer ? 'Your turn timed out.' : timeoutPlayer?.username ? `${timeoutPlayer.username} timed out.` : 'A player timed out.';
        this.enqueueDynamic(timeoutText, terminalEvent.eventId || `${normalizedGameId}:timeout`, 100, { ...terminalMeta, player: timeoutId });
      }
      if (Array.isArray(state?.players)) {
        for (const player of state.players) {
          const id = String(player.userId);
          if (player.isEliminated && this.previousEliminated.get(id) === false) {
            const name = player.username;
            const text = name ? `${name} is eliminated.` : 'A player is eliminated.';
            this.enqueueDynamic(text, `${normalizedGameId}:eliminated:${id}:${Number(player.missedTurns || 0)}`, 100, { ...terminalMeta, player: id });
          }
          this.previousEliminated.set(id, Boolean(player.isEliminated));
          this.previousMissedTurns.set(id, Number(player.missedTurns || 0));
        }
      }
      const winnerId = state?.winner?.userId ?? state?.winner;
      const winner = winnerId != null && Array.isArray(state?.players) ? state.players.find(p => String(p.userId) === String(winnerId)) : null;
      const winnerEventId = `${normalizedGameId}:game-end-voice:${winnerId ?? 'unknown'}`;
      if (winnerId != null && String(winnerId) === String(viewerId)) this.enqueue('youWin', winnerEventId, 100, { ...terminalMeta, player: winnerId });
      else this.enqueue('gameOver', winnerEventId, 100, terminalMeta);
      return true;
    }

    const event = state?.lastActionEvent;
    const timeoutPlayerId = event?.type === 'TURN_TIMEOUT' && event?.actorId != null ? String(event.actorId) : null;
    if (event?.eventId && event.eventId !== this.previousActionEventId) {
      this.previousActionEventId = String(event.eventId);
      this.observeAuthoritativeEvent(state, event, viewerId);
    }

    const currentTurn = state?.currentPlayerId == null ? null : String(state.currentPlayerId);
    if (currentTurn && String(viewerId ?? '') === currentTurn && this.previousTurnId !== null && this.previousTurnId !== currentTurn) {
      this.enqueue('yourTurn', `${normalizedGameId}:your-turn:${currentTurn}:${String(state?.turnStartTime || seq || '')}`, 80, { gameId: normalizedGameId, gameSeq: Number.isFinite(seq) ? seq : 0, turn: currentTurn, player: currentTurn });
    }
    this.previousTurnId = currentTurn;

    const players = Array.isArray(state?.players) ? state.players : [];
    for (const player of players) {
      const id = String(player.userId);
      const missed = Number(player.missedTurns || 0);
      const eliminated = Boolean(player.isEliminated);
      const beforeMissed = this.previousMissedTurns.get(id);
      const beforeEliminated = this.previousEliminated.get(id);
      if (beforeMissed != null && missed > beforeMissed && timeoutPlayerId !== id) {
        const eventId = `${normalizedGameId}:miss:${id}:${missed}`;
        const name = player.username;
        if (name) this.enqueueDynamic(`${name} missed their turn`, eventId, 80, { gameId: normalizedGameId, gameSeq: Number.isFinite(seq) ? seq : 0, turn: currentTurn, player: id });
      }
      if (beforeEliminated === false && eliminated === true) {
        const name = player.username;
        const text = name ? `${name} is eliminated.` : 'A player is eliminated.';
        this.enqueueDynamic(text, `${normalizedGameId}:eliminated:${id}:${missed}`, 85, { gameId: normalizedGameId, gameSeq: Number.isFinite(seq) ? seq : 0, player: id });
      }
      this.previousMissedTurns.set(id, missed);
      this.previousEliminated.set(id, eliminated);
    }

    // Announce Last Card only on an authoritative transition from more than
    // one card to exactly one. This avoids repeats from duplicate snapshots and
    // does not depend on the possibly stale player list attached to socket events.
    if (Array.isArray(state?.players)) {
      for (const player of state.players) {
        const id = String(player.userId);
        const rawCount = player.cardCount ?? (Array.isArray(player.hand) ? player.hand.length : null);
        const count = Number(rawCount);
        if (Number.isFinite(count) && count >= 0) {
          const previousCount = this.previousCardCounts.get(id);
          if (previousCount != null && previousCount > 1 && count === 1) {
            const actionSeq = Number.isFinite(seq) ? seq : this.latestGameSeq;
            this.enqueue('lastCard', `${normalizedGameId}:last-card-voice:${id}:${actionSeq}`, 92, {
              gameId: normalizedGameId, gameSeq: actionSeq, player: id,
            });
          }
          this.previousCardCounts.set(id, count);
        }
      }
    }

    return false;
  }

  observeAuthoritativeEvent(state, event, viewerId = null) {
    const eventId = event?.eventId ? String(event.eventId) : null;
    if (!eventId || this.processedEventIds.has(eventId)) return false;
    const gameId = String(state?.matchId ?? state?.playId ?? this.latestGameId);
    if (gameId && this.latestGameId && gameId !== this.latestGameId) return false;
    if (gameId && !this.latestGameId) this.latestGameId = gameId;
    const eventSeq = Number(event?.actionSequence);
    if (Number.isFinite(eventSeq) && eventSeq < this.latestAcceptedEventSeq) return false;
    if (Number.isFinite(eventSeq)) this.latestAcceptedEventSeq = eventSeq;
    this.processedEventIds.add(eventId);
    this.announceAuthoritativeEvent(state, event, viewerId);
    return true;
  }

  announceAuthoritativeEvent(state, event, viewerId = null) {
    const gameId = String(state?.matchId ?? state?.playId ?? this.latestGameId);
    const seq = Number(event?.actionSequence ?? state?.actionSequence);
    const meta = { gameId, gameSeq: Number.isFinite(seq) ? seq : this.latestGameSeq, player: event?.actorId || null, turn: state?.currentPlayerId ?? null, authoritativeEvent: true, authoritativeEventId: String(event.eventId) };

    if (event.type === 'GAME_STARTED') {
      this.gameStarted = true;
      this.enqueue('gameStarted', event.eventId, 40, meta);
      return;
    }

    if (event.type === 'CARD_PLAYED') {
      const actorId = event.actorId == null ? null : String(event.actorId);
      const actor = Array.isArray(state?.players) ? state.players.find(p => String(p.userId) === actorId) : null;
      const actorName = actor?.username || event?.actorUsername || null;
      const card = event.card;
      const specialType = event.specialType;

      if (specialType === 'WHOT') {
        const shapeKey = SHAPE_KEYS[String(event.requestedShape || '').toLowerCase()];
        if (shapeKey) this.enqueue(shapeKey, `${event.eventId}:shape`, 90, meta);
      } else if (specialType === 'PICK_TWO') {
        this.enqueue('pickTwo', event.eventId, 90, meta);
      } else if (specialType === 'GENERAL_MARKET') {
        this.enqueue('generalMarket', event.eventId, 90, meta);
      } else if (specialType === 'HOLD_ON') {
        this.enqueue('holdOn', event.eventId, 90, meta);
      } else if (specialType === 'SUSPENSION' || Number(card?.number) === 8) {
        this.enqueue('suspension', event.eventId, 90, meta);
      }
      if (event.terminal) this.enqueueTerminalResult(state, event, viewerId, meta);
      return;
    }

    if (event.type === 'CARD_DRAWN' || event.type === 'TURN_TIMEOUT') return;

    if (event.type === 'MATCH_TIMEOUT') {
      return;
    }

    if (event.eliminated && event.actorId != null) {
      const actorId = String(event.actorId);
      const player = Array.isArray(state?.players) ? state.players.find(p => String(p.userId) === actorId) : null;
      const text = player?.username ? player.username + ' is eliminated.' : 'A player is eliminated.';
      this.enqueueDynamic(text, event.eventId + ':elimination', 85, { ...meta, player: actorId });
    }
    if (event.terminal) this.enqueueTerminalResult(state, event, viewerId, meta);
  }

  enqueueTerminalResult(state, event, viewerId, meta = {}) {
    const winnerId = event?.winnerId ?? state?.winner?.userId ?? state?.winner;
    const winner = winnerId != null && Array.isArray(state?.players)
      ? state.players.find(p => String(p.userId) === String(winnerId))
      : null;
    const id = event.eventId + ':result';
    const viewer = viewerId == null ? null : String(viewerId);
    if (winnerId != null && viewer != null && String(winnerId) === viewer) {
      this.enqueue('youWin', id, 100, { ...meta, terminal: true, player: winnerId });
    } else {
      this.enqueue('gameOver', id, 100, { ...meta, terminal: true, player: winnerId });
    }
    this.terminal = true;
    this.terminalGameId = meta.gameId || this.latestGameId;
    this.terminalEventSeq = Number.isFinite(Number(meta.gameSeq)) ? Number(meta.gameSeq) : this.terminalEventSeq;
    this.latestGameStatus = 'finished';
  }

  enqueue(key, id, priority = undefined, meta = {}) {
    const text = TEXT[key];
    if (!this.enabled || !text || !id) return;
    this.enqueueItem({ key, text, id, priority, meta });
  }

  enqueueDynamic(text, id, priority = 10, meta = {}) {
    if (!this.enabled || typeof text !== 'string' || !text.trim() || !id) return;
    this.enqueueItem({ key: null, text: text.trim(), id, priority, meta });
  }

  enqueueItem({ key, text, id, priority, meta = {} }) {
    const gameId = meta?.gameId ? String(meta.gameId) : this.latestGameId;
    const gameSeq = Number(meta?.gameSeq);
    const final = key === 'youWin' || key === 'gameOver' || meta?.terminal === true;
    if (!this.enabled || !text || !id) return;
    if (this.terminal && !final && gameId === this.terminalGameId && Number.isFinite(gameSeq) && Number.isFinite(this.terminalEventSeq) && gameSeq > this.terminalEventSeq) return;
    if (gameId && this.latestGameId && gameId !== this.latestGameId) return;
    if (this.queue.some(x => x.id === id) || this.playingId === id) return;

    if (this.playing) this.interrupt();
    // Accepted events remain deduplicated by event ID, while obsolete pending speech is replaced by the newest live event.
    this.queue = [];

    const defaults = { youWin: 100, gameOver: 100, whot: 90, whotTriangle: 90, whotCircle: 90, whotSquare: 90, whotStar: 90, whotCross: 90, missTurn: 80, yourTurn: 80, pickTwo: 70, generalMarket: 70, holdOn: 70, continue: 10, gameStarted: 40 };
    const level = priority == null ? (defaults[key] ?? 10) : priority;
    const item = {
      key, text, id, priority: level, seq: ++this.sequence,
      gameSeq: Number.isFinite(gameSeq) ? gameSeq : this.latestGameSeq,
      gameId: gameId || this.latestGameId,
      generation: this.interruptGeneration, meta
    };
    this.queue.push(item);
    this.queue.sort((a, b) => {
      const as = Number.isFinite(a.gameSeq) ? a.gameSeq : Number.MAX_SAFE_INTEGER;
      const bs = Number.isFinite(b.gameSeq) ? b.gameSeq : Number.MAX_SAFE_INTEGER;
      return as - bs || a.seq - b.seq;
    });
    log('ANNOUNCER EVENT', { gameId: item.gameId, eventId: id, sequence: item.gameSeq, player: meta?.player, text, priority: level, queueLength: this.queue.length });
    this.drain();
  }

  interrupt() {
    this.interruptGeneration++;
    const activeAudio = this.playingAudio;
    this.playingAudio = null;
    this.playingUtterance = null;
    this.playing = false;
    this.playingId = null;
    this.playingItem = null;
    if (activeAudio) {
      try {
        activeAudio.onended = null;
        activeAudio.onerror = null;
        activeAudio.pause();
        activeAudio.currentTime = 0;
      } catch (_) {}
    }
    this.audio.forEach((a) => {
      try {
        a.onended = null;
        a.onerror = null;
        a.pause();
        a.currentTime = 0;
      } catch (_) {}
    });
    try { window.speechSynthesis?.cancel?.(); } catch (_) {}
    return true;
  }

  isStale(item) {
    if (!item) return true;
    if (item.gameId && this.latestGameId && item.gameId !== this.latestGameId) return true;
    if (item.generation !== this.interruptGeneration) return true;
    if (this.terminal && !item.meta?.terminal && item.gameId === this.terminalGameId &&
        Number.isFinite(item.gameSeq) && Number.isFinite(this.terminalEventSeq) &&
        item.gameSeq > this.terminalEventSeq) return true;
    return false;
  }

  drain() {
    if (this.playing || !this.enabled || !this.unlocked || !this.queue.length) return;
    this.queue.sort((a, b) => {
      const as = Number.isFinite(a.gameSeq) ? a.gameSeq : Number.MAX_SAFE_INTEGER;
      const bs = Number.isFinite(b.gameSeq) ? b.gameSeq : Number.MAX_SAFE_INTEGER;
      return as - bs || a.seq - b.seq;
    });
    let item = this.queue.shift();
    while (item && this.isStale(item)) item = this.queue.shift();
    if (!item) return;

    this.playing = true; this.playingId = item.id; this.playingItem = item; this.playingAudio = null; this.playingUtterance = null;
    const generation = this.interruptGeneration;
    let completed = false;
    const finish = () => {
      if (completed || generation !== this.interruptGeneration) return;
      completed = true;
      this.playing = false; this.playingId = null; this.playingItem = null; this.playingAudio = null; this.playingUtterance = null;
      this.drain();
    };
    const audio = item.key ? this.prepare(item.key) : null;
    if (audio) {
      this.playingAudio = audio;
      audio.volume = this.volume; audio.currentTime = 0;
      audio.onended = finish;
      audio.onerror = () => { this.lastError = 'Audio load failed: ' + VOICE_FILES[item.key]; finish(); };
      try {
        const promise = audio.play();
        promise?.catch?.((err) => { this.lastError = 'Audio play failed: ' + (err?.name || 'unknown'); finish(); });
      } catch (err) {
        this.lastError = 'Audio play failed: ' + (err?.name || 'unknown'); finish();
      }
      return;
    }
    this.speakFallback(item.text, finish, generation);
  }

  speakFallback(text, finish, generation) {
    if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance !== 'function') { finish(); return; }
    try {
      const u = new SpeechSynthesisUtterance(text);
      this.playingUtterance = u;
      u.volume = this.volume; u.rate = .94; u.pitch = 1.02;
      u.onstart = () => log('SPEECH START', text);
      u.onend = () => { if (generation === this.interruptGeneration) finish(); };
      u.onerror = () => { if (generation === this.interruptGeneration) finish(); };
      window.speechSynthesis.speak(u);
    } catch (_) { finish(); }
  }

  setTerminal(gameId, meta = {}) {
    const id = gameId == null ? this.latestGameId : String(gameId);
    if (id && this.latestGameId && id !== this.latestGameId) return false;
    if (id) this.latestGameId = id;
    if (this.terminal && this.terminalGameId === id) return this.terminalGeneration;
    this.terminal = true; this.latestGameStatus = 'finished'; this.terminalGameId = id || this.latestGameId;
    this.terminalEventSeq = Number.isFinite(Number(meta?.gameSeq)) ? Number(meta.gameSeq) : this.terminalEventSeq;
    this.terminalGeneration = this.interruptGeneration;
    log('TERMINAL ANNOUNCER RESET', { gameId: this.terminalGameId, actionSequence: meta.gameSeq ?? null });
    return this.terminalGeneration;
  }

  resetForGame(gameId) {
    const id = gameId == null ? null : String(gameId);
    this.stop();
    this.terminal = false; this.terminalGameId = null; this.latestGameId = id;
    this.latestGameSeq = 0; this.latestGameStatus = 'unknown'; this.terminalGeneration = this.interruptGeneration; this.terminalEventSeq = Number.POSITIVE_INFINITY;
    this.previousTurnId = null; this.previousMissedTurns = new Map(); this.previousEliminated = new Map(); this.previousCardCounts = new Map();
    this.previousActionEventId = null; this.processedEventIds = new Set(); this.latestAcceptedEventSeq = -1; this.gameStarted = false;
  }
}

const whotVoiceAnnouncer = new WhotVoiceAnnouncer();
export { VOICE_FILES, TEXT, WhotVoiceAnnouncer, isTerminalState };
export default whotVoiceAnnouncer;
